const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: The Complex Plane & Argand Diagram ─────────────────
function DiagramComplexPlane() {
  const W = 900, H = 380;
  // Left panel is 442px wide. Origin at cx=220, cy=195.
  // Use scale=56 so ±2.5 spans 140px each side — fits comfortably in 200px half-width.
  const cx = 220, cy = 195, sc = 56;
  const tx = x => cx + x * sc;
  const ty = y => cy - y * sc;

  // Keep all points strictly inside ±2.3 so nothing escapes the panel
  const z1 = { re: 1.6, im: 1.2, color: '#2E75B6', label: 'z₁ = 1.6+1.2i' };
  const z2 = { re: -1.0, im: 0.9, color: '#c0392b', label: 'z₂ = −1+0.9i' };
  const zSum = { re: z1.re+z2.re, im: z1.im+z2.im, color: '#27ae60', label: 'z₁+z₂ = 0.6+2.1i' };

  const r1 = Math.sqrt(z1.re**2 + z1.im**2);
  const th1 = Math.atan2(z1.im, z1.re);

  const Arrow = ({ x1,y1,x2,y2,color,width=2 }) => {
    const ang = Math.atan2(y2-y1, x2-x1);
    const hx = x2-9*Math.cos(ang), hy = y2-9*Math.sin(ang);
    return <g>
      <line x1={x1} y1={y1} x2={hx} y2={hy} stroke={color} strokeWidth={width}/>
      <polygon points={`${x2},${y2} ${x2-10*Math.cos(ang-0.4)},${y2-10*Math.sin(ang-0.4)} ${x2-10*Math.cos(ang+0.4)},${y2-10*Math.sin(ang+0.4)}`} fill={color}/>
    </g>;
  };

  // Right panel formula cards
  const px0 = 462, panW = W - px0 - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Complex Plane (Argand Diagram) — Rectangular & Polar Forms</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel */}
        <rect x={8} y={8} width={442} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={229} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Complex Number Anatomy &amp; Vector Addition</text>

        {/* Grid */}
        {[-3,-2,-1,0,1,2,3].map(v=>(
          <g key={v}>
            <line x1={tx(v)} y1={ty(-2.8)} x2={tx(v)} y2={ty(2.8)} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={tx(-3)} y1={ty(v)} x2={tx(3)} y2={ty(v)} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={tx(v)} y={cy+16} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
            {v!==0 && <text x={cx-6} y={ty(v)+4} textAnchor="end" fontSize="10" fill="#bbb">{v}</text>}
          </g>
        ))}

        {/* Axes */}
        <line x1={tx(-3)} y1={cy} x2={tx(3.1)} y2={cy} stroke="#1F3864" strokeWidth="1.6"/>
        <line x1={cx} y1={ty(2.8)} x2={cx} y2={ty(-2.8)} stroke="#1F3864" strokeWidth="1.6"/>
        <polygon points={`${tx(3.1)},${cy} ${tx(3.1)-8},${cy-4} ${tx(3.1)-8},${cy+4}`} fill="#1F3864"/>
        <polygon points={`${cx},${ty(2.8)} ${cx-4},${ty(2.8)+8} ${cx+4},${ty(2.8)+8}`} fill="#1F3864"/>
        <text x={tx(3.1)+7} y={cy+5} fontSize="14" fill="#1F3864" fontWeight="700">Re</text>
        <text x={cx+6} y={ty(2.8)-6} fontSize="14" fill="#1F3864" fontWeight="700">Im</text>

        {/* Unit circle */}
        <circle cx={cx} cy={cy} r={sc} fill="none" stroke="#ccc" strokeWidth="1" strokeDasharray="4,3"/>
        <text x={tx(1)+4} y={cy+14} fontSize="10" fill="#bbb">1</text>

        {/* z1 vector */}
        <Arrow x1={cx} y1={cy} x2={tx(z1.re)} y2={ty(z1.im)} color={z1.color} width={2.5}/>
        {/* Real and imag projections for z1 */}
        <line x1={tx(z1.re)} y1={cy} x2={tx(z1.re)} y2={ty(z1.im)} stroke={z1.color} strokeWidth="1.2" strokeDasharray="4,3"/>
        <line x1={cx} y1={ty(z1.im)} x2={tx(z1.re)} y2={ty(z1.im)} stroke={z1.color} strokeWidth="1.2" strokeDasharray="4,3"/>
        <circle cx={tx(z1.re)} cy={ty(z1.im)} r={5} fill={z1.color} stroke="#fff" strokeWidth="2"/>
        <text x={tx(z1.re)+8} y={ty(z1.im)-8} fontSize="12" fill={z1.color} fontWeight="700">{z1.label}</text>

        {/* Magnitude arc */}
        {(() => {
          const arcPts = [];
          for(let a=0; a<=th1; a+=0.05) arcPts.push([tx(0.55*Math.cos(a)), ty(0.55*Math.sin(a))]);
          const d=arcPts.map((p,i)=>`${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <>
            <path d={d} fill="none" stroke={z1.color} strokeWidth="1.4"/>
            <text x={tx(0.68*Math.cos(th1/2))} y={ty(0.68*Math.sin(th1/2))} fontSize="11" fill={z1.color} fontWeight="700">θ₁</text>
          </>;
        })()}
        {/* |z1| label along vector */}
        <text x={(cx+tx(z1.re))/2-20} y={(cy+ty(z1.im))/2-8} fontSize="11" fill={z1.color} fontWeight="700">|z₁|={r1.toFixed(2)}</text>
        {/* Real/imag labels */}
        <text x={tx(z1.re)} y={cy+16} textAnchor="middle" fontSize="10" fill={z1.color}>Re=2</text>
        <text x={tx(-0.3)} y={ty(z1.im)+4} fontSize="10" fill={z1.color}>Im=1.5</text>

        {/* z2 vector */}
        <Arrow x1={cx} y1={cy} x2={tx(z2.re)} y2={ty(z2.im)} color={z2.color} width={2.5}/>
        <circle cx={tx(z2.re)} cy={ty(z2.im)} r={5} fill={z2.color} stroke="#fff" strokeWidth="2"/>
        <text x={tx(z2.re)-12} y={ty(z2.im)-10} fontSize="12" fill={z2.color} fontWeight="700">{z2.label}</text>

        {/* Vector addition parallelogram */}
        <line x1={tx(z1.re)} y1={ty(z1.im)} x2={tx(zSum.re)} y2={ty(zSum.im)} stroke="#27ae60" strokeWidth="1.4" strokeDasharray="5,3"/>
        <line x1={tx(z2.re)} y1={ty(z2.im)} x2={tx(zSum.re)} y2={ty(zSum.im)} stroke="#27ae60" strokeWidth="1.4" strokeDasharray="5,3"/>
        <Arrow x1={cx} y1={cy} x2={tx(zSum.re)} y2={ty(zSum.im)} color={zSum.color} width={2.8}/>
        <circle cx={tx(zSum.re)} cy={ty(zSum.im)} r={5} fill={zSum.color} stroke="#fff" strokeWidth="2"/>
        <text x={tx(zSum.re)+8} y={ty(zSum.im)-8} fontSize="12" fill={zSum.color} fontWeight="700">{zSum.label}</text>

        {/* Right panel */}
        <rect x={px0} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={px0+panW/2} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Forms of a Complex Number</text>

        {[
          { y:34,  bg:'#EBF3FB', bc:'#2E75B6', title:'Rectangular Form', lines:['z = a + bi', 'a = Re(z) = real part', 'b = Im(z) = imaginary part', 'i = √(−1)  (i² = −1)'] },
          { y:142, bg:'#f0fff4', bc:'#27ae60', title:'Polar Form', lines:['z = r∠θ  =  r(cosθ + i sinθ)', 'r = |z| = √(a²+b²)  (modulus)', 'θ = arg(z) = atan2(b,a)  (argument)', 'θ in radians; range (−π, π]'] },
          { y:254, bg:'#fff7ee', bc:'#e07b00', title:'Euler Form  (most powerful)', lines:['z = r·e^(iθ)', 'From: e^(iθ) = cosθ + i sinθ', 'Connects exponential and trig', 'Makes multiplication trivial'] },
        ].map(({y,bg,bc,title,lines})=>(
          <g key={title}>
            <rect x={px0+8} y={y} width={panW-16} height={100} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            <text x={px0+16} y={y+17} fontSize="12" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln,i)=><text key={i} x={px0+16} y={y+34+i*18} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Every complex number z = a+bi corresponds to a point (a,b) in the complex plane — the real part on the horizontal axis, the imaginary part on the vertical. As a vector from the origin, z has magnitude r = |z| = √(a²+b²) and angle θ = arg(z) = atan2(b,a). Addition of complex numbers is exactly vector addition: add real parts and imaginary parts separately. The three forms — rectangular, polar, and Euler — are equivalent and each is most convenient for different operations.
      </div>
    </div>
  );
}

// ── Diagram 2: Euler's Formula — The Unit Circle Connection ───────
function DiagramEulersFormula() {
  const W = 900, H = 380;
  const cx = 210, cy = 195, r = 140;
  const tx = a => cx + r*Math.cos(a);
  const ty = a => cy - r*Math.sin(a);

  // Angle to demonstrate
  const theta = Math.PI/3; // 60°
  const Px = tx(theta), Py = ty(theta);

  // cos/sin wave panels on right
  const waveX0 = 480, waveW = 400, waveH = 120;
  const wCx = t => waveX0 + (t/(2*Math.PI))*waveW;
  const wCy1 = y => 80 - y*45;
  const wCy2 = y => 220 - y*45;

  const cosWave=[], sinWave=[];
  for(let t=0;t<=2*Math.PI;t+=0.06){
    cosWave.push([wCx(t), wCy1(Math.cos(t))]);
    sinWave.push([wCx(t), wCy2(Math.sin(t))]);
  }
  const wd = pts => pts.map((p,i)=>`${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  // Rotating point trace at current angle
  const thetaLine = [];
  for(let t=0; t<=theta; t+=0.04) thetaLine.push([wCx(t), wCy1(Math.cos(t))]);
  const thetaLine2 = [];
  for(let t=0; t<=theta; t+=0.04) thetaLine2.push([wCx(t), wCy2(Math.sin(t))]);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Euler's Formula  e^(iθ) = cos θ + i sin θ — Unit Circle to Waves</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left: unit circle */}
        <rect x={8} y={8} width={448} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={228} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Point e^(iθ) Traces the Unit Circle</text>

        {/* Axes */}
        {[-1,0,1].map(v=>(
          <g key={v}>
            <text x={cx+v*r} y={cy+18} textAnchor="middle" fontSize="11" fill="#bbb">{v}</text>
            {v!==0&&<text x={cx-14} y={cy-v*r+4} textAnchor="end" fontSize="11" fill="#bbb">{v}</text>}
          </g>
        ))}
        <line x1={cx-r-20} y1={cy} x2={cx+r+20} y2={cy} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={cx} y1={cy+r+20} x2={cx} y2={cy-r-20} stroke="#1F3864" strokeWidth="1.5"/>
        <polygon points={`${cx+r+20},${cy} ${cx+r+12},${cy-4} ${cx+r+12},${cy+4}`} fill="#1F3864"/>
        <polygon points={`${cx},${cy-r-20} ${cx-4},${cy-r-12} ${cx+4},${cy-r-12}`} fill="#1F3864"/>
        <text x={cx+r+26} y={cy+5} fontSize="13" fill="#1F3864" fontWeight="700">Re</text>
        <text x={cx+6} y={cy-r-22} fontSize="13" fill="#1F3864" fontWeight="700">Im</text>

        {/* Unit circle */}
        <circle cx={cx} cy={cy} r={r} fill="#EBF3FB" fillOpacity="0.4" stroke="#2E75B6" strokeWidth="2"/>

        {/* Angle arc */}
        {(() => {
          const arcPts=[];
          for(let a=0;a<=theta;a+=0.04) arcPts.push([cx+42*Math.cos(a), cy-42*Math.sin(a)]);
          const d=arcPts.map((p,i)=>`${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <>
            <path d={d} fill="none" stroke="#e07b00" strokeWidth="2"/>
            <text x={cx+52*Math.cos(theta/2)} y={cy-52*Math.sin(theta/2)} fontSize="13" fill="#e07b00" fontWeight="700">θ</text>
          </>;
        })()}

        {/* Radius vector */}
        <line x1={cx} y1={cy} x2={Px} y2={Py} stroke="#2E75B6" strokeWidth="2.5"/>
        <circle cx={Px} cy={Py} r={6} fill="#2E75B6" stroke="#fff" strokeWidth="2"/>

        {/* cos projection (horizontal) */}
        <line x1={cx} y1={Py} x2={Px} y2={Py} stroke="#c0392b" strokeWidth="1.8" strokeDasharray="5,3"/>
        <line x1={cx+r*Math.cos(theta)} y1={cy} x2={cx+r*Math.cos(theta)} y2={Py} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="3,2"/>
        <text x={cx+r*Math.cos(theta)+6} y={cy+16} fontSize="11" fill="#c0392b" fontWeight="700">cosθ</text>

        {/* sin projection (vertical) */}
        <line x1={Px} y1={cy} x2={Px} y2={Py} stroke="#27ae60" strokeWidth="1.8" strokeDasharray="5,3"/>
        <line x1={cx} y1={Py} x2={cx-8} y2={Py} stroke="#27ae60" strokeWidth="1.5"/>
        <text x={cx-12} y={Py+4} textAnchor="end" fontSize="11" fill="#27ae60" fontWeight="700">sinθ</text>

        {/* Point label */}
        <text x={Px+10} y={Py-10} fontSize="12" fill="#2E75B6" fontWeight="700">e^(iθ)</text>
        <text x={Px+10} y={Py+6} fontSize="11" fill="#555" fontFamily="'Courier New',monospace">=(cosθ, sinθ)</text>

        {/* Quadrant labels */}
        {[{a:Math.PI/4,l:'θ=π/4'},{a:3*Math.PI/4,l:'θ=3π/4'},{a:-3*Math.PI/4,l:'θ=−3π/4'},{a:-Math.PI/4,l:'θ=−π/4'}].map(({a,l},i)=>(
          <text key={i} x={cx+(r+22)*Math.cos(a)} y={cy-(r+22)*Math.sin(a)} textAnchor="middle" fontSize="9.5" fill="#bbb">{l}</text>
        ))}

        {/* Special points */}
        {[
          {a:0,       label:'1',       col:'#1F3864'},
          {a:Math.PI/2, label:'i',     col:'#27ae60'},
          {a:Math.PI,  label:'−1',     col:'#c0392b'},
          {a:-Math.PI/2,label:'−i',    col:'#9b59b6'},
        ].map(({a,label,col})=>(
          <g key={label}>
            <circle cx={cx+r*Math.cos(a)} cy={cy-r*Math.sin(a)} r={5} fill={col} stroke="#fff" strokeWidth="1.5"/>
            <text x={cx+(r+18)*Math.cos(a)} y={cy-(r+18)*Math.sin(a)+4} textAnchor="middle" fontSize="12" fill={col} fontWeight="700">{label}</text>
          </g>
        ))}

        {/* Right: waves */}
        <rect x={468} y={8} width={424} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={680} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Projections Generate cos &amp; sin Waves</text>

        {/* Cos panel */}
        <rect x={476} y={36} width={408} height={120} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={484} y={52} fontSize="11" fill="#c0392b" fontWeight="700">Re(e^(iθ)) = cos θ</text>
        <line x1={waveX0} y1={wCy1(0)} x2={waveX0+waveW} y2={wCy1(0)} stroke="#c0392b" strokeWidth="1" strokeDasharray="3,2" opacity="0.4"/>
        <path d={wd(cosWave)} fill="none" stroke="#c0392b" strokeWidth="2.2"/>
        <line x1={wCx(theta)} y1={36} x2={wCx(theta)} y2={156} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={wCx(theta)} cy={wCy1(Math.cos(theta))} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        {[0, Math.PI/2, Math.PI, 3*Math.PI/2, 2*Math.PI].map((t,i)=>(
          <text key={i} x={wCx(t)} y={152} textAnchor="middle" fontSize="9" fill="#bbb">
            {['0','π/2','π','3π/2','2π'][i]}
          </text>
        ))}

        {/* Sin panel */}
        <rect x={476} y={168} width={408} height={120} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={484} y={184} fontSize="11" fill="#27ae60" fontWeight="700">Im(e^(iθ)) = sin θ</text>
        <line x1={waveX0} y1={wCy2(0)} x2={waveX0+waveW} y2={wCy2(0)} stroke="#27ae60" strokeWidth="1" strokeDasharray="3,2" opacity="0.4"/>
        <path d={wd(sinWave)} fill="none" stroke="#27ae60" strokeWidth="2.2"/>
        <line x1={wCx(theta)} y1={168} x2={wCx(theta)} y2={288} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={wCx(theta)} cy={wCy2(Math.sin(theta))} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        {[0, Math.PI/2, Math.PI, 3*Math.PI/2, 2*Math.PI].map((t,i)=>(
          <text key={i} x={wCx(t)} y={284} textAnchor="middle" fontSize="9" fill="#bbb">
            {['0','π/2','π','3π/2','2π'][i]}
          </text>
        ))}

        {/* Euler identity box */}
        <rect x={476} y={300} width={408} height={68} rx={4} fill="#1F3864" fillOpacity="0.06" stroke="#1F3864" strokeWidth="1.5"/>
        <text x={680} y={320} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Euler's Identity (most beautiful equation)</text>
        <text x={680} y={344} textAnchor="middle" fontSize="15" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">e^(iπ) + 1 = 0</text>
        <text x={680} y={362} textAnchor="middle" fontSize="10.5" fill="#555">Links e, i, π, 1, and 0 in one equation — θ=π gives e^(iπ)=cosπ+i sinπ=−1</text>
      </svg>
      <div style={S.dCaption}>
        Euler's formula e^(iθ) = cosθ + i sinθ is the central identity of complex analysis. It reveals that the unit circle in the complex plane is the graph of the complex exponential — as θ increases, the point e^(iθ) rotates around the circle at unit radius. The real projection traces out a cosine wave; the imaginary projection traces a sine wave. This is why cosines and sines appear in every oscillating system in physics and engineering: they are the real and imaginary parts of rotating complex exponentials.
      </div>
    </div>
  );
}

// ── Diagram 3: Complex Arithmetic ────────────────────────────────
function DiagramComplexArithmetic() {
  const W = 900, H = 380;

  // Panel layout: 4 equal panels
  const PW = (W-28)/2 - 3;
  const p = i => 8 + i*(PW+8);

  const cx1 = p(0)+PW/2, cy1 = 200, sc1 = 50;
  const tx1 = x=>cx1+x*sc1, ty1 = y=>cy1-y*sc1;

  const cx2 = p(1)+PW/2, cy2 = 200, sc2 = 50;
  const tx2 = x=>cx2+x*sc2, ty2 = y=>cy2-y*sc2;

  // z1 = 2∠(π/4), z2 = 1.5∠(π/6)
  const r1=2, th1=Math.PI/4;
  const r2=1.5, th2=Math.PI/6;
  const z1re=r1*Math.cos(th1), z1im=r1*Math.sin(th1);
  const z2re=r2*Math.cos(th2), z2im=r2*Math.sin(th2);
  // Product: r1*r2 ∠ (th1+th2)
  const rp=r1*r2, thp=th1+th2;
  const zpre=rp*Math.cos(thp), zpim=rp*Math.sin(thp);
  // Quotient: (r1/r2) ∠ (th1-th2)
  const rq=r1/r2, thq=th1-th2;
  const zqre=rq*Math.cos(thq), zqim=rq*Math.sin(thq);

  const Arrow=({x1,y1,x2,y2,color,w=2.2})=>{
    const ang=Math.atan2(y2-y1,x2-x1);
    const hx=x2-9*Math.cos(ang),hy=y2-9*Math.sin(ang);
    return <g>
      <line x1={x1} y1={y1} x2={hx} y2={hy} stroke={color} strokeWidth={w}/>
      <polygon points={`${x2},${y2} ${x2-10*Math.cos(ang-0.4)},${y2-10*Math.sin(ang-0.4)} ${x2-10*Math.cos(ang+0.4)},${y2-10*Math.sin(ang+0.4)}`} fill={color}/>
    </g>;
  };

  const miniCircle=(cx,cy,sc)=>(
    <circle cx={cx} cy={cy} r={sc} fill="none" stroke="#e8e8e8" strokeWidth="1" strokeDasharray="3,3"/>
  );

  const axes=(cx,cy,sc,lab)=>(
    <g>
      <line x1={cx-sc-14} y1={cy} x2={cx+sc+20} y2={cy} stroke="#1F3864" strokeWidth="1.3"/>
      <line x1={cx} y1={cy+sc+14} x2={cx} y2={cy-sc-18} stroke="#1F3864" strokeWidth="1.3"/>
      <text x={cx+sc+22} y={cy+4} fontSize="11" fill="#1F3864" fontWeight="700">Re</text>
      <text x={cx+5} y={cy-sc-20} fontSize="11" fill="#1F3864" fontWeight="700">Im</text>
    </g>
  );

  const angleArc=(cx,cy,r2,a1,a2,col)=>{
    if(Math.abs(a2-a1)<0.02) return null;
    const pts=[];
    const step=Math.sign(a2-a1)*0.05;
    for(let a=a1;Math.sign(a2-a)*Math.sign(step)>0;a+=step) pts.push([cx+r2*Math.cos(a),cy-r2*Math.sin(a)]);
    pts.push([cx+r2*Math.cos(a2),cy-r2*Math.sin(a2)]);
    return <path d={pts.map((pt,i)=>`${i===0?'M':'L'}${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(' ')} fill="none" stroke={col} strokeWidth="1.5"/>;
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Complex Arithmetic — Multiplication Adds Angles, Division Subtracts Them</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* ── Left top: Multiplication ── */}
        <rect x={p(0)} y={8} width={PW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(0)+PW/2} y={27} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Multiplication: z₁·z₂ = r₁r₂ ∠(θ₁+θ₂)</text>

        {miniCircle(cx1,cy1,sc1)}
        {axes(cx1,cy1,sc1)}

        <Arrow x1={cx1} y1={cy1} x2={tx1(z1re)} y2={ty1(z1im)} color="#2E75B6"/>
        <text x={tx1(z1re)+6} y={ty1(z1im)-8} fontSize="11" fill="#2E75B6" fontWeight="700">z₁ (r={r1}, θ=45°)</text>

        <Arrow x1={cx1} y1={cy1} x2={tx1(z2re)} y2={ty1(z2im)} color="#27ae60"/>
        <text x={tx1(z2re)+6} y={ty1(z2im)+14} fontSize="11" fill="#27ae60" fontWeight="700">z₂ (r={r2}, θ=30°)</text>

        {/* Product arc showing angle addition */}
        {angleArc(cx1,cy1,30,0,th1,'#2E75B6')}
        {angleArc(cx1,cy1,30,th1,thp,'#27ae60')}
        <Arrow x1={cx1} y1={cy1} x2={tx1(zpre)} y2={ty1(zpim)} color="#c0392b" w={2.8}/>
        <circle cx={tx1(zpre)} cy={ty1(zpim)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <text x={tx1(zpre)+6} y={ty1(zpim)-8} fontSize="11" fill="#c0392b" fontWeight="700">z₁z₂ (r={rp.toFixed(2)}, θ=75°)</text>

        <rect x={p(0)+8} y={H-88} width={PW-16} height={72} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={p(0)+16} y={H-72} fontSize="11.5" fill="#1F3864" fontWeight="700">z₁·z₂ = r₁r₂ e^(i(θ₁+θ₂))</text>
        <text x={p(0)+16} y={H-53} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Magnitudes multiply: r₁×r₂={rp.toFixed(2)}</text>
        <text x={p(0)+16} y={H-35} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Angles add: 45°+30°=75°</text>
        <text x={p(0)+16} y={H-17} fontSize="10.5" fill="#555">Rectangular: expand using i²=−1, collect terms</text>

        {/* ── Right: Division ── */}
        <rect x={p(1)} y={8} width={PW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(1)+PW/2} y={27} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Division: z₁/z₂ = (r₁/r₂) ∠(θ₁−θ₂)</text>

        {miniCircle(cx2,cy2,sc2)}
        {axes(cx2,cy2,sc2)}

        <Arrow x1={cx2} y1={cy2} x2={tx2(z1re)} y2={ty2(z1im)} color="#2E75B6"/>
        <text x={tx2(z1re)+6} y={ty2(z1im)-8} fontSize="11" fill="#2E75B6" fontWeight="700">z₁</text>
        <Arrow x1={cx2} y1={cy2} x2={tx2(z2re)} y2={ty2(z2im)} color="#27ae60"/>
        <text x={tx2(z2re)+6} y={ty2(z2im)+14} fontSize="11" fill="#27ae60" fontWeight="700">z₂</text>

        {angleArc(cx2,cy2,30,th2,th1,'#9b59b6')}
        <Arrow x1={cx2} y1={cy2} x2={tx2(zqre)} y2={ty2(zqim)} color="#c0392b" w={2.8}/>
        <circle cx={tx2(zqre)} cy={ty2(zqim)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <text x={tx2(zqre)+6} y={ty2(zqim)-8} fontSize="11" fill="#c0392b" fontWeight="700">z₁/z₂ (r≈{rq.toFixed(2)}, θ=15°)</text>

        <rect x={p(1)+8} y={H-88} width={PW-16} height={72} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={p(1)+16} y={H-72} fontSize="11.5" fill="#1F3864" fontWeight="700">z₁/z₂ = (r₁/r₂) e^(i(θ₁−θ₂))</text>
        <text x={p(1)+16} y={H-53} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Magnitudes divide: {r1}/{r2}={rq.toFixed(2)}</text>
        <text x={p(1)+16} y={H-35} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Angles subtract: 45°−30°=15°</text>
        <text x={p(1)+16} y={H-17} fontSize="10.5" fill="#555">Rectangular: multiply by conjugate z₂*/|z₂|²</text>
      </svg>
      <div style={S.dCaption}>
        Polar/Euler form makes multiplication and division geometric: multiplying two complex numbers multiplies their magnitudes and adds their angles; dividing subtracts angles. This is why complex exponentials are so powerful — multiplying e^(iα)·e^(iβ) = e^(i(α+β)) just adds angles, which is exactly what rotating a vector does. In rectangular form the same operations require expanding and collecting i²=−1 terms, or rationalising by the conjugate.
      </div>
    </div>
  );
}

// ── Diagram 4: Calculus with Complex Exponentials ─────────────────
function DiagramComplexCalculus() {
  const W = 900, H = 390;

  // Right: three time-domain signals from complex exponentials
  // e^(st) where s = σ+iω
  const nPts = 300;
  const tMax = 4*Math.PI;
  const ts = Array.from({length:nPts},(_,k)=>k/(nPts-1)*tMax);

  // Signal 1: pure oscillation  s=iω, σ=0
  const sig1 = ts.map(t=>Math.cos(t));
  // Signal 2: decaying oscillation  σ=-0.3
  const sig2 = ts.map(t=>Math.exp(-0.3*t)*Math.cos(t));
  // Signal 3: growing oscillation  σ=+0.15
  const sig3 = ts.map(t=>Math.exp(0.15*t)*Math.cos(t));

  // Right panel: three equal-height sub-panels stacked inside the right box
  // Right box: x=470..892 (w=422), y=8..382 (h=374). Title row=30px. 3 panels share 344px.
  // Each panel: h=114px. Bases at 30+14+57=101, 30+114+14+57=215, 30+228+14+57=329.
  const panelH = 110;
  const panelTop = (i) => 36 + i * panelH + 4;   // top of sub-panel inside right box (SVG coords)
  const panelBase = (i) => panelTop(i) + panelH/2; // zero-line of each panel
  const panelAmp = 38;                              // amplitude in px – never reaches panel edge

  const wcx = t => 484 + (t / tMax) * 390;
  const wcy = (y, base, amp) => base - y * amp;

  const sigPanels = [
    { sig: sig1, idx: 0, color: '#2E75B6', title: 's=iω  (σ=0):  pure oscillation',   formula: 'Re(e^(iωt)) = cos(ωt)',         envelopeA: null  },
    { sig: sig2, idx: 1, color: '#27ae60', title: 's=−0.3+iω  (σ<0):  decaying',       formula: 'e^(σt)cos(ωt),  σ<0 → damps', envelopeA: -0.3  },
    { sig: sig3, idx: 2, color: '#c0392b', title: 's=+0.15+iω  (σ>0):  growing',       formula: 'e^(σt)cos(ωt),  σ>0 → grows', envelopeA: 0.15  },
  ];

  const sigPath = (sig, base, amp) => {
    // Clamp to prevent wild overflow on growing signal
    const pts = ts.map((t, i) => {
      const y = Math.max(-1.1, Math.min(1.1, sig[i]));
      return [wcx(t), wcy(y, base, amp)];
    });
    return pts.map((p, k) => `${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Calculus of Complex Exponentials — Derivatives, Integrals &amp; the s-Domain</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left: derivative/integral rules */}
        <rect x={8} y={8} width={450} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={233} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Calculus Rules for e^(st)  where s = σ + iω</text>

        {[
          {y:34,  bg:'#EBF3FB', bc:'#2E75B6', title:'Derivative Rule',
           lines:['d/dt [e^(st)] = s·e^(st)',
                  'd/dt [e^(iωt)] = iω·e^(iωt)',
                  'Multiplying by s rotates & scales the vector',
                  'Derivative of cos(ωt) → −ωsin(ωt)  (phase −90°)']},
          {y:138, bg:'#f0fff4', bc:'#27ae60', title:'Integral Rule',
           lines:['∫e^(st) dt = (1/s)·e^(st) + C',
                  '∫e^(iωt) dt = (1/iω)·e^(iωt) + C',
                  '1/iω = −i/ω  (divides magnitude, rotates +90°)',
                  'Integral of cos(ωt) → (1/ω)sin(ωt)  (phase +90°)']},
          {y:242, bg:'#fff7ee', bc:'#e07b00', title:'Why This Matters: Superposition',
           lines:['cos(ωt) = Re(e^(iωt)) = (e^(iωt)+e^(−iωt))/2',
                  'sin(ωt) = Im(e^(iωt)) = (e^(iωt)−e^(−iωt))/2i',
                  'All trig identities follow from exponential algebra',
                  "d/dt[cos] = Re(iω·e^(iωt)) = −ω sin(ωt)  ✓"]},
          {y:330, bg:'#fff0f0', bc:'#c0392b', title:'Characteristic Exponent s = σ + iω',
           lines:['σ = real part → growth (σ>0) or decay (σ<0)',
                  'ω = imaginary part → oscillation frequency',
                  'σ=0: neutral;  σ<0: stable;  σ>0: unstable',
                  'Every LTI system response is a sum of e^(sₖt) terms']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={title}>
            <rect x={16} y={y} width={432} height={90} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            <text x={24} y={y+16} fontSize="12" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln,i)=><text key={i} x={24} y={y+33+i*17} fontSize="10.5" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>)}
          </g>
        ))}

        {/* Right: signal panels */}
        <rect x={470} y={8} width={W-470-8} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={470+(W-470-8)/2} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Three Cases of Re(e^(st))</text>

        {sigPanels.map(({sig, idx, color, title, formula, envelopeA}) => {
          const base = panelBase(idx);
          const amp  = panelAmp;
          const top  = panelTop(idx);
          const bot  = top + panelH;
          const bgColors = ['#EBF3FB','#f0fff4','#fff0f0'];
          return (
            <g key={idx}>
              <rect x={478} y={top} width={W-478-14} height={panelH} rx={3}
                fill={bgColors[idx]} fillOpacity="0.6" stroke={color} strokeWidth="1"/>
              {/* Zero line */}
              <line x1={wcx(0)} y1={base} x2={wcx(tMax)} y2={base} stroke="#ccc" strokeWidth="1" strokeDasharray="3,2"/>
              {/* Envelope */}
              {envelopeA && (() => {
                const envT = ts.map(t => [wcx(t), wcy(Math.min(1.1, Math.exp(envelopeA*t)), base, amp)]);
                const envB = ts.map(t => [wcx(t), wcy(Math.max(-1.1,-Math.exp(envelopeA*t)), base, amp)]);
                const dT = envT.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
                const dB = envB.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
                return <>
                  <path d={dT} fill="none" stroke={color} strokeWidth="1.2" strokeDasharray="5,3" opacity="0.55"/>
                  <path d={dB} fill="none" stroke={color} strokeWidth="1.2" strokeDasharray="5,3" opacity="0.55"/>
                </>;
              })()}
              {/* Signal curve */}
              <path d={sigPath(sig, base, amp)} fill="none" stroke={color} strokeWidth="2"/>
              <text x={484} y={top+14} fontSize="10.5" fill={color} fontWeight="700">{title}</text>
              <text x={484} y={top+27} fontSize="9.5"  fill="#555"  fontFamily="'Courier New',monospace">{formula}</text>
              {/* t labels */}
              {[0,1,2,3,4].map(k => (
                <text key={k} x={wcx(k*Math.PI)} y={bot-2} textAnchor="middle" fontSize="8.5" fill="#bbb">{k}π</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The complex exponential e^(st) with s = σ+iω is the universal building block of linear systems. Its derivative is just s times itself — making it an eigenfunction of differentiation. The real part σ controls whether the envelope grows or decays; the imaginary part ω controls the oscillation frequency. All three time-domain behaviours — neutral oscillation, stable decay, and unstable growth — are simply different positions of s in the complex plane, which is exactly what the s-plane (Laplace domain) exploits.
      </div>
    </div>
  );
}

// ── Diagram 5: Phasors ───────────────────────────────────────────
function DiagramPhasors() {
  const W = 900, H = 400;

  // Scale phasors to fit inside the left panel (450px wide, 400px tall).
  // Origin at cx=200, cy=200. With sc=38, ±3.5 maps to ±133px — safe.
  // Use R=2.4, XL=3.0, XC=1.8 so Vtot.mag ≈ 3.25 (< 3.5)
  const R=2.4, XL=3.0, XC=1.8;
  const VR={ re:R,   im:0,    mag:R,  ang:0,         color:'#2E75B6', label:'V_R=IR' };
  const VL={ re:0,   im:XL,   mag:XL, ang:Math.PI/2, color:'#c0392b', label:'V_L=IXL' };
  const VC={ re:0,   im:-XC,  mag:XC, ang:-Math.PI/2,color:'#27ae60', label:'V_C=IXC' };
  const Vnet_im = XL - XC;
  const Vtot={ re:R, im:Vnet_im, mag:Math.sqrt(R*R+Vnet_im*Vnet_im),
               ang:Math.atan2(Vnet_im,R), color:'#e07b00', label:'V_total' };

  const cx=210, cy=210, sc=40;
  const tx = x => cx + x * sc;
  const ty = y => cy - y * sc;

  const Arrow=({x1,y1,x2,y2,color,w=2.5,dash})=>{
    const ang=Math.atan2(y2-y1,x2-x1);
    const hx=x2-9*Math.cos(ang), hy=y2-9*Math.sin(ang);
    return <g>
      <line x1={x1} y1={y1} x2={hx} y2={hy} stroke={color} strokeWidth={w} strokeDasharray={dash}/>
      <polygon points={`${x2},${y2} ${x2-10*Math.cos(ang-0.4)},${y2-10*Math.sin(ang-0.4)} ${x2-10*Math.cos(ang+0.4)},${y2-10*Math.sin(ang+0.4)}`} fill={color}/>
    </g>;
  };

  // Right panel: 4 equal waveform sub-panels inside x=458..892 (w=434), y=8..392 (h=384)
  // Title row ≈ 30px. 4 panels share 350px → each 87px tall.
  const tMax = 2*Math.PI;
  const wts = Array.from({length:200},(_,k)=>k/199*tMax);
  const wPanelH = 85;
  const wPanelTop = i => 36 + i * wPanelH;
  const wBase     = i => wPanelTop(i) + wPanelH/2;
  const wAmp      = 30;  // px — safely within half-panel
  const wxL = 462, wxR = 888;
  const wx  = t => wxL + (t/tMax)*(wxR - wxL);
  const wy  = (y, base) => base - y * wAmp;

  // Normalise each waveform to amplitude 1 so they all look uniform
  const wavePanels = [
    { fn: t => Math.cos(t),                     color:'#2E75B6', label:'v_R(t)  in phase with I' },
    { fn: t => Math.cos(t + Math.PI/2),          color:'#c0392b', label:'v_L(t)  leads I by 90°' },
    { fn: t => Math.cos(t - Math.PI/2),          color:'#27ae60', label:'v_C(t)  lags  I by 90°' },
    { fn: t => Math.cos(t + Vtot.ang),           color:'#e07b00', label:`v_tot(t)  φ=${(Vtot.ang*180/Math.PI).toFixed(1)}°` },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Phasors — Series RLC: Adding Voltages as Complex Vectors</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left: phasor diagram */}
        <rect x={8} y={8} width={450} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={229} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Phasor Diagram — Impedance &amp; Voltage Relationships</text>

        {/* Grid */}
        {[-4,-3,-2,-1,0,1,2,3,4].map(v=>(
          <g key={v}>
            <line x1={tx(v)} y1={50} x2={tx(v)} y2={H-50} stroke="#f5f5f5" strokeWidth="1"/>
            <line x1={tx(-4)} y1={ty(v)} x2={tx(5)} y2={ty(v)} stroke="#f5f5f5" strokeWidth="1"/>
          </g>
        ))}

        {/* Axes */}
        <line x1={tx(-4.5)} y1={cy} x2={tx(4.8)} y2={cy} stroke="#1F3864" strokeWidth="1.4"/>
        <line x1={cx} y1={50} x2={cx} y2={H-45} stroke="#1F3864" strokeWidth="1.4"/>
        <text x={tx(4.8)+6} y={cy+5} fontSize="12" fill="#1F3864" fontWeight="700">Re</text>
        <text x={cx+5} y={48} fontSize="12" fill="#1F3864" fontWeight="700">Im</text>

        {/* Axes labels: leads/lags */}
        <text x={cx+8} y={ty(3.8)} fontSize="10" fill="#888" fontStyle="italic">leads (inductive)</text>
        <text x={cx+8} y={ty(-3.8)+12} fontSize="10" fill="#888" fontStyle="italic">lags (capacitive)</text>

        {/* I (reference phasor) */}
        <Arrow x1={cx} y1={cy} x2={tx(1.6)} y2={cy} color="#888" w={1.8} dash="5,3"/>
        <text x={tx(1.6)+6} y={cy+4} fontSize="11" fill="#888" fontWeight="700">I (reference)</text>

        {/* V_R along Re axis */}
        <Arrow x1={cx} y1={cy} x2={tx(VR.re)} y2={ty(VR.im)} color={VR.color} w={3}/>
        <text x={tx(VR.re)+6} y={ty(VR.im)+14} fontSize="12" fill={VR.color} fontWeight="700">{VR.label}</text>

        {/* V_L along +Im */}
        <Arrow x1={cx} y1={cy} x2={tx(VL.re)} y2={ty(VL.im)} color={VL.color} w={3}/>
        <text x={tx(VL.re)+8} y={ty(VL.im)-4} fontSize="12" fill={VL.color} fontWeight="700">{VL.label}</text>

        {/* V_C along -Im */}
        <Arrow x1={cx} y1={cy} x2={tx(VC.re)} y2={ty(VC.im)} color={VC.color} w={3}/>
        <text x={tx(VC.re)+8} y={ty(VC.im)+14} fontSize="12" fill={VC.color} fontWeight="700">{VC.label}</text>

        {/* Construction lines for V_total */}
        <line x1={tx(VR.re)} y1={ty(VR.im)} x2={tx(Vtot.re)} y2={ty(Vtot.im)} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <line x1={tx(0)} y1={ty(Vnet_im)} x2={tx(Vtot.re)} y2={ty(Vtot.im)} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>

        {/* V_total */}
        <Arrow x1={cx} y1={cy} x2={tx(Vtot.re)} y2={ty(Vtot.im)} color={Vtot.color} w={3.2}/>
        <circle cx={tx(Vtot.re)} cy={ty(Vtot.im)} r={5} fill={Vtot.color} stroke="#fff" strokeWidth="2"/>
        <text x={tx(Vtot.re)+8} y={ty(Vtot.im)-8} fontSize="12" fill={Vtot.color} fontWeight="700">{Vtot.label}={Vtot.mag.toFixed(2)}∠{(Vtot.ang*180/Math.PI).toFixed(1)}°</text>

        {/* Phase angle arc */}
        {(()=>{
          const pts=[];
          for(let a=0;a<=Vtot.ang;a+=0.04) pts.push([tx(1.2*Math.cos(a)),ty(1.2*Math.sin(a))]);
          const d=pts.map((p,i)=>`${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <>
            <path d={d} fill="none" stroke="#e07b00" strokeWidth="1.5"/>
            <text x={tx(1.45*Math.cos(Vtot.ang/2))} y={ty(1.45*Math.sin(Vtot.ang/2))} fontSize="11" fill="#e07b00" fontWeight="700">φ</text>
          </>;
        })()}

        {/* Right: waveforms */}
        <rect x={458} y={8} width={434} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={675} y={27} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Corresponding Time-Domain Waveforms</text>

        {wavePanels.map(({fn, color, label}, i) => {
          const base = wBase(i);
          const top  = wPanelTop(i);
          const pts  = wts.map(t => [wx(t), wy(fn(t), base)]);
          const d    = pts.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          const bgColors = ['#f0f4ff','#fff0f0','#f0fff4','#fff8ee'];
          return (
            <g key={i}>
              <rect x={462} y={top} width={wxR-wxL+26} height={wPanelH} rx={2}
                fill={bgColors[i]} stroke="#e8e8e8" strokeWidth="0.8"/>
              <line x1={wx(0)} y1={base} x2={wx(tMax)} y2={base} stroke="#ddd" strokeWidth="1"/>
              <path d={d} fill="none" stroke={color} strokeWidth="2"/>
              <text x={470} y={top+13} fontSize="10.5" fill={color} fontWeight="700">{label}</text>
              {[0, Math.PI, 2*Math.PI].map((t,k)=>(
                <text key={k} x={wx(t)} y={top+wPanelH-4} textAnchor="middle" fontSize="8.5" fill="#bbb">
                  {['0','π','2π'][k]}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        A phasor is a complex number V = |V|e^(jφ) that represents a sinusoidal signal's amplitude and phase. In a series RLC circuit, the resistor voltage is in phase with current (V_R = IR), the inductor voltage leads by 90° (V_L = jωLI), and the capacitor voltage lags by 90° (V_C = I/jωC). Adding these as complex vectors gives the total voltage phasor — one complex addition replaces three separate trigonometric calculations. The phase angle φ of V_total relative to I is the circuit's power factor angle.
      </div>
    </div>
  );
}

// ── Diagram 6: Impedance & Frequency Response ────────────────────
function DiagramImpedance() {
  const W = 900, H = 390;

  const R=3, L=1, C=0.1;
  const w0 = 1/Math.sqrt(L*C);  // ≈ 3.16
  const nw = 200;
  const ws = Array.from({length:nw},(_,k)=>0.3+k*(10/nw));
  const Zmag   = ws.map(w=>{ const X=w*L-1/(w*C); return Math.sqrt(R*R+X*X); });
  const Zphase = ws.map(w=>{ const X=w*L-1/(w*C); return Math.atan2(X,R)*180/Math.PI; });

  // ── Layout ──────────────────────────────────────────────────────
  // Left panel: x=8..484 (w=476), y=8..382 (h=374)
  // Two sub-plots stacked:
  //   Magnitude  plot: y=36..196  (h=160)  — shows |Z| 0..10
  //   Phase      plot: y=206..370 (h=164)  — shows ∠Z -90..90°
  const lx = 70;          // left x of plot area (room for y-axis labels)
  const rx = 476;         // right x of plot area

  // Magnitude sub-plot
  const m_top=42, m_bot=192, m_yMin=0, m_yMax=10;
  const mScX = (rx-lx)/10;                              // px per rad/s
  const mScY = (m_bot-m_top)/(m_yMax-m_yMin);           // px per Ω
  const mTwx = w  => lx + w*mScX;
  const mTwy = y  => m_bot - (y-m_yMin)*mScY;

  // Phase sub-plot
  const p_top=206, p_bot=372, p_yMin=-90, p_yMax=90;
  const pScX = (rx-lx)/10;
  const pScY = (p_bot-p_top)/(p_yMax-p_yMin);
  const pTwx = w  => lx + w*pScX;
  const pTwy = ang => p_bot - (ang-p_yMin)*pScY;

  const toD = pts => pts.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  // Cap Zmag at 9.5 so the U-shape stays inside the plot area
  const magPath  = ws.map((w,i)=>[mTwx(w), mTwy(Math.min(Zmag[i],9.5))]);
  const phPath   = ws.map((w,i)=>[pTwx(w), pTwy(Math.max(-89,Math.min(89,Zphase[i])))]);

  const px0=500, panW=W-px0-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Impedance Z = R + jX — Magnitude &amp; Phase Response of Series RLC (R=3Ω, L=1H, C=0.1F)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left: Bode-like plots */}
        <rect x={8} y={8} width={476} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={246} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">|Z(ω)| and ∠Z(ω) vs Frequency</text>

        {/* ── Magnitude plot ── */}
        <rect x={lx} y={m_top} width={rx-lx} height={m_bot-m_top} fill="#fafcff" stroke="#e8e8e8" strokeWidth="0.8"/>
        <text x={lx-6} y={m_top-4} fontSize="11" fill="#1F3864" fontWeight="700" textAnchor="end">|Z| (Ω)</text>
        {[0,2,4,6,8,10].map(v=>(
          <g key={v}>
            <line x1={lx} y1={mTwy(v)} x2={rx} y2={mTwy(v)} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={lx-4} y={mTwy(v)+4} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>
          </g>
        ))}
        {[0,2,4,6,8,10].map(w=>(
          <text key={w} x={mTwx(w)} y={m_bot+12} textAnchor="middle" fontSize="9" fill="#bbb">{w}</text>
        ))}
        {/* Resonance line */}
        <line x1={mTwx(w0)} y1={m_top} x2={mTwx(w0)} y2={m_bot} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={mTwx(w0)+4} y={m_top+12} fontSize="10" fill="#c0392b" fontWeight="700">ω₀={w0.toFixed(2)}</text>
        {/* Mag curve */}
        <path d={toD(magPath)} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#magClip)"/>
        <defs><clipPath id="magClip"><rect x={lx} y={m_top} width={rx-lx} height={m_bot-m_top}/></clipPath></defs>
        {/* Min point */}
        <circle cx={mTwx(w0)} cy={mTwy(R)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={mTwx(w0)+8} y={mTwy(R)-6} fontSize="10.5" fill="#c0392b" fontWeight="700">|Z|_min = R = {R}Ω</text>

        {/* ── Phase plot ── */}
        <rect x={lx} y={p_top} width={rx-lx} height={p_bot-p_top} fill="#f9fff9" stroke="#e8e8e8" strokeWidth="0.8"/>
        <text x={lx-6} y={p_top-4} fontSize="11" fill="#1F3864" fontWeight="700" textAnchor="end">∠Z (°)</text>
        {[-90,-45,0,45,90].map(v=>(
          <g key={v}>
            <line x1={lx} y1={pTwy(v)} x2={rx} y2={pTwy(v)} stroke={v===0?"#ccc":"#f0f0f0"} strokeWidth={v===0?1.2:1}/>
            <text x={lx-4} y={pTwy(v)+4} textAnchor="end" fontSize="9" fill="#bbb">{v}°</text>
          </g>
        ))}
        {[0,2,4,6,8,10].map(w=>(
          <text key={w} x={pTwx(w)} y={p_bot+12} textAnchor="middle" fontSize="9" fill="#bbb">{w}</text>
        ))}
        <line x1={pTwx(w0)} y1={p_top} x2={pTwx(w0)} y2={p_bot} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3"/>
        <defs><clipPath id="phClip"><rect x={lx} y={p_top} width={rx-lx} height={p_bot-p_top}/></clipPath></defs>
        <path d={toD(phPath)} fill="none" stroke="#27ae60" strokeWidth="2.5" clipPath="url(#phClip)"/>
        <circle cx={pTwx(w0)} cy={pTwy(0)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={pTwx(w0)+8} y={pTwy(0)-6} fontSize="10.5" fill="#c0392b" fontWeight="700">∠Z=0° at ω₀</text>
        <text x={(lx+rx)/2} y={p_bot+24} textAnchor="middle" fontSize="10" fill="#888" fontStyle="italic">ω (rad/s) →</text>

        {/* Right: impedance cards */}
        <rect x={px0} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={px0+panW/2} y={27} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Component Impedances</text>

        {[
          {y:34,  bg:'#EBF3FB', bc:'#2E75B6', title:'Resistor  Z_R',
           lines:['Z_R = R','Real impedance: no phase shift','V in phase with I  (∠Z=0°)','Same at all frequencies']},
          {y:128, bg:'#fff0f0', bc:'#c0392b', title:'Inductor  Z_L',
           lines:['Z_L = jωL = ωL∠+90°','Imaginary +j: voltage leads current','Increases with frequency','High-pass behaviour']},
          {y:222, bg:'#f0fff4', bc:'#27ae60', title:'Capacitor  Z_C',
           lines:['Z_C = 1/(jωC) = (1/ωC)∠−90°','Imaginary −j: voltage lags current','Decreases with frequency','Low-pass behaviour']},
          {y:316, bg:'#fff7ee', bc:'#e07b00', title:'Series RLC  Z_total',
           lines:['Z = R + j(ωL − 1/ωC)','Resonance: ωL=1/ωC → Z=R (min)',`ω₀ = 1/√(LC) = ${w0.toFixed(2)} rad/s`,'Q = ω₀L/R = bandwidth sharpness']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={title}>
            <rect x={px0+8} y={y} width={panW-16} height={82} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            <text x={px0+16} y={y+16} fontSize="12" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln,i)=><text key={i} x={px0+16} y={y+32+i*16} fontSize="10.5" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Impedance Z = R + jX is the complex generalisation of resistance. The real part R dissipates energy; the imaginary part X (reactance) stores and returns it. At resonance ω₀ = 1/√(LC), the inductive and capacitive reactances cancel exactly (Z_L = −Z_C), leaving only resistance — this is the minimum-impedance, maximum-current condition. The phase plot shows Z transitioning from −90° (capacitive, below ω₀) through 0° (resistive, at ω₀) to +90° (inductive, above ω₀).
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function ComplexNumbersPhasors() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Complex Numbers &amp; Phasors</h1>
      <p style={S.p}>
        Complex numbers extend the real number line into a plane by introducing i = √(−1). Far from being abstract curiosities, they are the most efficient language for describing anything that oscillates or rotates. Fourier transforms, AC circuit analysis, vibration modes, control system stability, and quantum mechanics all depend on them. This page builds from the geometric meaning of complex numbers through Euler's formula to phasors and impedance — the engineering toolkit for sinusoidal steady-state analysis.
      </p>

      <InfoBox title="The central insight: rotation in the complex plane IS oscillation in time" items={[
        "i rotates a vector 90° counterclockwise — multiplying by i is a 90° rotation",
        "e^(iθ) = cosθ + i sinθ — rotating a unit vector to angle θ",
        "e^(iωt) — a point rotating around the unit circle at angular speed ω rad/s",
        "Re(e^(iωt)) = cos(ωt) — the cosine is just the horizontal projection of that rotation",
        "Phasor: freeze the rotation, represent signal by amplitude and phase angle alone",
        "Impedance Z = V/I in complex form — generalises Ohm's law to all AC components",
      ]}/>

      <h2 style={S.h2}>The Complex Plane</h2>
      <DiagramComplexPlane/>
      <Table2 rows={[
        ["Imaginary unit i","i = √(−1);  i² = −1;  i³ = −i;  i⁴ = 1\nPowers of i cycle with period 4"],
        ["Rectangular form","z = a + bi\na = Re(z) = real part;  b = Im(z) = imaginary part"],
        ["Modulus (magnitude)","r = |z| = √(a² + b²)\nDistance from origin in the complex plane"],
        ["Argument (angle)","θ = arg(z) = atan2(b, a)\nAngle from positive Re-axis;  range: (−π, π]"],
        ["Polar form","z = r(cosθ + i sinθ) = r∠θ\nMost useful for multiplication and powers"],
        ["Euler form","z = re^(iθ)\nMost compact; most powerful for calculus"],
        ["Complex conjugate","z* = a − bi  (flip sign of imaginary part)\n|z|² = z·z* = a² + b²;  Re(z) = (z+z*)/2"],
        ["Addition/subtraction","(a+bi) ± (c+di) = (a±c) + (b±d)i\nVector addition/subtraction in the complex plane"],
        ["Equality","z₁ = z₂ ↔ Re(z₁)=Re(z₂) AND Im(z₁)=Im(z₂)\nTwo equations from one complex equation"],
      ]}/>

      <h2 style={S.h2}>Euler's Formula</h2>
      <DiagramEulersFormula/>
      <Table2 rows={[
        ["Euler's formula","e^(iθ) = cosθ + i sinθ\nProof: match Taylor series of e^x, cos x, sin x"],
        ["Euler's identity","e^(iπ) + 1 = 0  (θ = π)\nLinks five fundamental constants; cos π=−1, sin π=0"],
        ["Unit circle","|e^(iθ)| = 1 for all real θ\nEvery point on the unit circle is e^(iθ) for some θ"],
        ["Cosine from Euler","cosθ = Re(e^(iθ)) = (e^(iθ) + e^(−iθ)) / 2\nCosine is average of two counter-rotating phasors"],
        ["Sine from Euler","sinθ = Im(e^(iθ)) = (e^(iθ) − e^(−iθ)) / (2i)\nSine is the difference of the same two phasors"],
        ["Trig identities via Euler","cos(A+B) = Re(e^(i(A+B))) = Re(e^(iA)·e^(iB))\nExpand product → cos A cos B − sin A sin B  (no memorisation)"],
        ["de Moivre's Theorem","(cosθ + i sinθ)ⁿ = cos(nθ) + i sin(nθ)\nDirectly from (e^(iθ))ⁿ = e^(inθ)"],
        ["nth roots of unity","z = e^(i2πk/n),  k=0,1,…,n−1\nEvenly spaced around the unit circle — used in FFT"],
      ]}/>

      <h2 style={S.h2}>Complex Arithmetic</h2>
      <DiagramComplexArithmetic/>
      <Table2 rows={[
        ["Multiplication (rectangular)","(a+bi)(c+di) = (ac−bd) + (ad+bc)i\nUse i²=−1 to collect; tedious for large angles"],
        ["Multiplication (polar/Euler)","r₁e^(iθ₁) · r₂e^(iθ₂) = r₁r₂ e^(i(θ₁+θ₂))\nMagnitudes multiply; angles add — far simpler"],
        ["Division (rectangular)","(a+bi)/(c+di) = (a+bi)(c−di) / (c²+d²)\nMultiply numerator and denominator by conjugate c−di"],
        ["Division (polar)","(r₁/r₂) e^(i(θ₁−θ₂))\nMagnitudes divide; angles subtract"],
        ["Powers  zⁿ","(re^(iθ))ⁿ = rⁿ e^(inθ)\nMagnitude raised to power; angle multiplied by n"],
        ["Square root  √z","√(re^(iθ)) = √r · e^(iθ/2)\nTwo roots: k=0 and k=1 → angles θ/2 and θ/2+π"],
        ["Conjugate rules","(z₁z₂)* = z₁*z₂*;  (z₁/z₂)* = z₁*/z₂*\n|z₁z₂| = |z₁||z₂|;  arg(z₁z₂) = arg z₁ + arg z₂"],
        ["Multiplying by i","i·z rotates z by 90° CCW\ni² rotates 180° (negation);  i³ rotates 270° (= −90°)"],
      ]}/>

      <h2 style={S.h2}>Calculus with Complex Exponentials</h2>
      <DiagramComplexCalculus/>
      <Table2 rows={[
        ["Derivative of e^(st)","d/dt[e^(st)] = s·e^(st)\ne^(st) is an eigenfunction of d/dt with eigenvalue s"],
        ["Derivative of e^(iωt)","d/dt[e^(iωt)] = iω·e^(iωt)\nMultiplying by iω: scales by ω, rotates vector 90° CCW"],
        ["d/dt[cosωt] via Euler","d/dt Re(e^(iωt)) = Re(iω·e^(iωt)) = Re(ω·e^(i(ωt+π/2)))\n= −ω sinωt  ✓ — derivative = rotation by 90° CCW"],
        ["Integral of e^(iωt)","∫e^(iωt) dt = (1/iω)e^(iωt) = (−i/ω)e^(iωt)\n1/iω divides magnitude by ω and rotates 90° clockwise"],
        ["∫cosωt via Euler","∫Re(e^(iωt)) dt = Re(e^(iωt)/iω) = (1/ω)sinωt  ✓"],
        ["s = σ + iω (complex frequency)","σ controls envelope: σ<0 → decay;  σ>0 → growth;  σ=0 → neutral\nω controls oscillation frequency"],
        ["LTI system response","Input e^(st) → output H(s)·e^(st)\nH(s) = transfer function evaluated at complex frequency s"],
        ["Fourier transform link","X(ω) = ∫x(t)e^(−iωt) dt\nDecomposes signal into complex exponentials at each frequency ω"],
        ["Laplace transform link","X(s) = ∫x(t)e^(−st) dt\nGeneralises Fourier by allowing σ≠0 — handles transients"],
      ]}/>

      <h2 style={S.h2}>Phasors</h2>
      <DiagramPhasors/>
      <Table2 rows={[
        ["Phasor definition","V = |V|e^(jφ) = |V|∠φ\nRepresents sinusoidal v(t) = |V|cos(ωt+φ);\ntime dependence e^(jωt) suppressed"],
        ["Phasor from time signal","v(t) = A cos(ωt+φ)  →  V = A∠φ\nAmplitude becomes modulus; phase angle becomes argument"],
        ["Time signal from phasor","v(t) = Re(V · e^(jωt)) = Re(Ae^(j(ωt+φ)))\nMultiply phasor by e^(jωt) and take real part"],
        ["Addition of phasors","V_total = V₁ + V₂  (complex addition)\nReplaces sum of sinusoids — no trig identities needed"],
        ["KVL with phasors","V_R + V_L + V_C = V_source\nAll complex arithmetic; valid because all same frequency ω"],
        ["Resistor phasor","V_R = R·I  (in phase — both real when I is reference)\nNo phase shift between voltage and current"],
        ["Inductor phasor","V_L = jωL·I = ωL·I∠+90°\nVoltage leads current by 90°"],
        ["Capacitor phasor","V_C = I/(jωC) = (I/ωC)∠−90°\nVoltage lags current by 90°"],
        ["Power factor","cos φ where φ = arg(V_total) − arg(I)\nφ=0: purely resistive (max power);  φ=±90°: purely reactive (no power)"],
      ]}/>

      <h2 style={S.h2}>Impedance &amp; Frequency Response</h2>
      <DiagramImpedance/>
      <Table2 rows={[
        ["Impedance Z","Z = V/I  (phasor ratio, complex Ohm's law)\nZ = R + jX;  R=resistance, X=reactance"],
        ["Reactance X","X = Im(Z)\nX>0: inductive (voltage leads);  X<0: capacitive (voltage lags)"],
        ["Impedance magnitude","|Z| = √(R² + X²)\nRatio of voltage amplitude to current amplitude"],
        ["Impedance angle","∠Z = atan2(X, R)\nPhase difference: voltage angle minus current angle"],
        ["Series combination","Z_total = Z₁ + Z₂ + …\nComplex addition — same as series resistance in DC"],
        ["Parallel combination","1/Z_total = 1/Z₁ + 1/Z₂ + …\nAdmittances Y = 1/Z add in parallel"],
        ["Resonance ω₀","ω₀ = 1/√(LC)\nX_L = X_C → X=0 → Z=R (minimum, purely real)"],
        ["Quality factor Q","Q = ω₀L/R = 1/(ω₀CR)\nBandwidth BW = ω₀/Q;  high Q → sharp resonance peak"],
        ["Transfer function H(jω)","H(jω) = V_out/V_in  (complex ratio of phasors)\n|H|: gain at frequency ω;  ∠H: phase shift at frequency ω"],
        ["Bode plot","log|H(jω)| vs log ω  (magnitude in dB = 20log|H|)\n∠H(jω) vs log ω;  standard tool for frequency response analysis"],
      ]}/>

      <InfoBox title="Key Takeaway — Why Complex Numbers Are Indispensable in Engineering" items={[
        "Rotation and oscillation are the same thing — e^(iωt) unifies them in one symbol",
        "Derivative d/dt in time domain → multiply by jω in frequency domain (algebraic!)",
        "Integral ∫dt in time domain → divide by jω in frequency domain",
        "Trig identities follow automatically from e^(iA)·e^(iB) = e^(i(A+B)) — no memorisation",
        "Phasors convert AC circuit differential equations into complex algebraic equations",
        "Impedance Z = R+jX generalises Ohm's law: same rules (series, parallel, dividers) apply",
        "Resonance = imaginary parts cancel → only real resistance remains → maximum current",
        "Laplace/Fourier transforms decompose any signal into complex exponentials — then superpose",
      ]}/>
    </div>
  );
}